import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ResolvedAddress } from '../../../services/address-lookup.service';
import { OrganisationService } from '../../../services/organisation.service';
import {
  COUNTRIES,
  countryName,
  resolveCountryCode,
} from '../../../utils/countries.util';

@Component({
  selector: 'app-organisation-address-form',
  templateUrl: './organisation-address-form.component.html',
  styleUrl: './organisation-address-form.component.scss',
})
export class OrganisationAddressFormComponent {
  form!: FormGroup;
  isEditing = false;
  countries = COUNTRIES;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    protected organisationService: OrganisationService
  ) {
    this.form = this.fb.group({
      street: ['', [Validators.required]],
      number: ['', [Validators.required]],
      zip: ['', [Validators.required]],
      city: ['', [Validators.required]],
      // The picker and the select both write the ISO-2 code; `country` is
      // derived from it on submit and stays the human-readable display value.
      country_code: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.organisationService.organisation$.subscribe((org) => {
      if (org) {
        this.form.patchValue({
          street: org.street || '',
          number: org.number || '',
          zip: org.zip || '',
          city: org.city || '',
          // Rows written before migration 027 only have the free-text country.
          country_code:
            org.country_code || resolveCountryCode(org.country) || '',
        });
      }
    });
  }

  /** Fill the fields from a geocoder hit. Everything stays editable afterwards
   * — Photon's house-number coverage is patchy outside cities. */
  applyResolved(address: ResolvedAddress) {
    this.form.patchValue({
      street: address.street || '',
      number: address.number || '',
      zip: address.zip || '',
      city: address.city || '',
      country_code: address.country_code || '',
    });
    this.form.markAsDirty();
  }

  get countryLabelLang() {
    return this.translate.currentLang || this.translate.defaultLang || 'de';
  }

  countryLabel(code: string) {
    return countryName(code, this.countryLabelLang);
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    const code = this.form.value.country_code;

    this.organisationService
      .updateMyOrganisation({
        ...this.form.value,
        // Keep the display column in step so existing views and the AVV
        // renderer, which read `country`, do not go stale.
        country: countryName(code, this.countryLabelLang),
      })
      .subscribe((data) => {
        this.isEditing = false;
      });
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }
}
